import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { WA_HERO, wa } from "@/lib/site";
import { EASE } from "@/components/Reveal";
import { Monogram } from "@/components/Monogram";

const HEADLINE_LINES = [
    { text: "Seu negócio não precisa", gold: false },
    { text: "apenas estar na internet.", gold: false },
    { text: "Precisa estar preparado", gold: true },
    { text: "para vender.", gold: true },
];

const PILLARS = ["DIAGNÓSTICO", "ESTRATÉGIA", "ESTRUTURA", "COMUNICAÇÃO"];

const LineReveal = ({ text, gold, delay }) => (
    <span className="block overflow-hidden pb-2 -mb-2">
        <motion.span
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.05, delay, ease: EASE }}
            className={`block font-display font-extrabold tracking-tight leading-[1.14] text-[2.5rem] sm:text-5xl lg:text-[3.4rem] ${
                gold ? "text-gold-grad" : "text-ink"
            }`}
        >
            {text}
        </motion.span>
    </span>
);

const Schematic = () => {
    const nodes = [
        { label: PILLARS[0], x: "50%", y: "7%" },
        { label: PILLARS[1], x: "93%", y: "50%" },
        { label: PILLARS[2], x: "50%", y: "93%" },
        { label: PILLARS[3], x: "7%", y: "50%" },
    ];
    return (
        <div className="relative w-full max-w-[500px] aspect-square mx-auto select-none" aria-hidden="true">
            <div className="absolute inset-0 rounded-full border border-dashed border-[#2a2a36] animate-spin-slower" />
            <div className="absolute inset-[14%] rounded-full border border-gold/15" />
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" fill="none">
                {[
                    [50, 50, 50, 10],
                    [50, 50, 90, 50],
                    [50, 50, 50, 90],
                    [50, 50, 10, 50],
                ].map((l, i) => (
                    <line
                        key={i}
                        x1={l[0]}
                        y1={l[1]}
                        x2={l[2]}
                        y2={l[3]}
                        stroke="#D4AF37"
                        strokeOpacity="0.35"
                        strokeWidth="0.3"
                        className="dash-flow"
                    />
                ))}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-[26%] aspect-square rounded-2xl border border-gold/40 bg-[#0c0c10] shadow-[0_0_60px_rgba(212,175,55,0.12)] flex items-center justify-center animate-float-y">
                    <Monogram className="w-[55%] h-[55%]" />
                </div>
            </div>
            {nodes.map((n, i) => (
                <div
                    key={n.label}
                    className="absolute -translate-x-1/2 -translate-y-1/2"
                    style={{ left: n.x, top: n.y }}
                >
                    <div
                        className="flex items-center gap-2 rounded-full border border-line bg-[#0d0d12]/90 px-3.5 py-2"
                        style={{ animation: `float-y ${5 + i}s ease-in-out infinite`, animationDelay: `${i * 0.7}s` }}
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                        <span className="font-mono2 text-[10px] tracking-[0.2em] text-ink2 whitespace-nowrap">
                            {n.label}
                        </span>
                    </div>
                </div>
            ))}
        </div>
    );
};

const Hero = () => {
    const { scrollY } = useScroll();
    const ySchematic = useTransform(scrollY, [0, 600], [0, 90]);
    const yCopy = useTransform(scrollY, [0, 600], [0, 40]);

    return (
        <section id="inicio" data-testid="hero-section" className="relative overflow-hidden">
            <div className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_70%_60%_at_50%_35%,black,transparent)]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_45%_at_50%_0%,rgba(212,175,55,0.13),transparent_70%)]" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-bg" />

            <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-36 sm:pt-44 pb-20 lg:pt-48 lg:pb-28 grid lg:grid-cols-[1.3fr_0.7fr] gap-14 lg:gap-8 items-center">
                <motion.div style={{ y: yCopy }} className="flex flex-col gap-8">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE }}
                        className="flex items-center gap-3"
                    >
                        <span className="h-px w-10 bg-gold/70" />
                        <p className="font-mono2 text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-gold">
                            Estratégia + Tecnologia + Presença Digital + Resultado
                        </p>
                    </motion.div>

                    <h1 className="flex flex-col gap-1.5">
                        {HEADLINE_LINES.map((line, i) => (
                            <LineReveal key={line.text} text={line.text} gold={line.gold} delay={0.25 + i * 0.16} />
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
                        className="text-base sm:text-lg text-ink2 leading-relaxed max-w-xl"
                    >
                        A SETH organiza sua presença digital para transformar Instagram, WhatsApp e
                        comunicação em uma estrutura mais profissional, estratégica e preparada para
                        gerar oportunidades.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1, ease: EASE }}
                        className="flex flex-col sm:flex-row gap-4"
                    >
                        <a
                            href={WA_HERO}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-primary-cta"
                            className="btn-gold relative inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-sm font-semibold tracking-wide"
                        >
                            Falar com a SETH pelo WhatsApp
                            <ArrowUpRight size={16} />
                        </a>
                        <a
                            href="#solucoes"
                            data-testid="hero-secondary-cta"
                            className="btn-ghost inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-sm font-semibold tracking-wide text-ink border border-line"
                        >
                            Conhecer as soluções
                        </a>
                    </motion.div>
                </motion.div>

                <motion.div
                    style={{ y: ySchematic }}
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.2, delay: 0.7, ease: EASE }}
                    data-testid="hero-schematic"
                    className="hidden lg:block"
                >
                    <Schematic />
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 1 }}
                className="relative hidden lg:flex items-center justify-center pb-10 gap-3 text-ink3"
            >
                <span className="font-mono2 text-[10px] uppercase tracking-[0.3em]">Role para descobrir</span>
                <ArrowDown size={12} className="animate-bounce" />
            </motion.div>
        </section>
    );
};

export default Hero;
